import React from 'react';
import {
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesDataTable,
  AgnesDateRangePickerInput,
  AgnesLabel,
  AgnesPageHeader,
  AgnesPopover,
  AgnesPopoverContent,
  AgnesPopoverTrigger,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
} from '@agnes/ui';
import { Download, Filter, ScrollText, Trash2, X } from 'lucide-react';
import { ExportButton } from '@/components/ExportButton';
import { useAuditoriaStore } from '@/stores/useAuditoriaStore';
import { EmptyState } from '@/components/ui/EmptyState';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

type Item = any;

// Radix Select não aceita item com value vazio; sentinela p/ "Todos".
const ALL = '__all__';
const SELECT_TRIGGER =
  '[&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate [&>[data-slot=select-value]]:text-left';

const dataBR = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '');
const horaBR = (iso?: string) => (iso ? new Date(iso).toLocaleTimeString('pt-BR') : '');

// string[] de datas ISO do date-range picker -> {fromTime, toTime}. Vazio -> undefined.
const parsePeriodo = (periodo: string[]): { fromTime?: string; toTime?: string } => {
  if (!periodo || periodo.length === 0) return {};
  return { fromTime: periodo[0], toTime: periodo[periodo.length - 1] };
};

function FiltroSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <AgnesLabel>{label}</AgnesLabel>
      <AgnesSelect value={value || ALL} onValueChange={onChange}>
        <AgnesSelectTrigger className={SELECT_TRIGGER}>
          <AgnesSelectValue placeholder="Todos" />
        </AgnesSelectTrigger>
        <AgnesSelectContent>
          <AgnesSelectItem value={ALL}>Todos</AgnesSelectItem>
          {options.map((o) => (
            <AgnesSelectItem key={o} value={o}>
              {o}
            </AgnesSelectItem>
          ))}
        </AgnesSelectContent>
      </AgnesSelect>
    </div>
  );
}

// Popover de filtro (padrão das outras tabelas: botão "Filtro" -> suspenso com
// campos + Aplicar/Limpar). Estado local do form; só escreve no store ao aplicar.
function AuditoriaFiltro() {
  const opcoes = useAuditoriaStore((s) => s.opcoes);
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState<{
    periodo: string[];
    nome: string;
    ip: string;
    localizacao: string;
    atividades: string;
  }>({
    periodo: [],
    nome: '',
    ip: '',
    localizacao: '',
    atividades: '',
  });
  const set = (k: Exclude<keyof typeof f, 'periodo'>) => (v: string) =>
    setF((s) => ({ ...s, [k]: v }));
  // valor '' (nenhum) mapeia pro sentinela ALL do Radix Select.
  const setSel = (k: Exclude<keyof typeof f, 'periodo'>) => (v: string) =>
    set(k)(v === ALL ? '' : v);

  const aplicar = () => {
    const { fromTime, toTime } = parsePeriodo(f.periodo);
    useAuditoriaStore.getState().setFiltros({
      fromTime,
      toTime,
      userNames: f.nome ? [f.nome] : undefined,
      ipAddresses: f.ip ? [f.ip] : undefined,
      services: f.localizacao ? [f.localizacao] : undefined,
      actions: f.atividades ? [f.atividades] : undefined,
    });
    useAuditoriaStore.getState().buscar();
    setOpen(false);
  };

  const limpar = () => {
    setF({ periodo: [], nome: '', ip: '', localizacao: '', atividades: '' });
    useAuditoriaStore.getState().setFiltros({});
    useAuditoriaStore.getState().buscar();
  };

  return (
    <AgnesPopover open={open} onOpenChange={setOpen}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          variant="outlined"
          className="!bg-agnes-bg-default"
          startIcon={<Filter className="h-4 w-4" />}
        >
          Filtro
        </AgnesButton>
      </AgnesPopoverTrigger>
      <AgnesPopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-agnes-border px-4 py-3">
          <span className="text-base font-semibold text-agnes-text">Filtro</span>
          <button
            type="button"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-agnes-text-muted hover:text-agnes-text"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto px-4 py-4">
          <div>
            <AgnesDateRangePickerInput
              value={f.periodo}
              onChange={(dates) => setF((s) => ({ ...s, periodo: dates }))}
              label="Período"
              dataQa="auditoria-filtro-periodo"
            />
            <p className="mt-1 text-xs text-agnes-text-muted">Preencha um período de até 60 dias</p>
          </div>
          <FiltroSelect
            label="Nome do usuário"
            value={f.nome}
            options={opcoes.users}
            onChange={setSel('nome')}
          />
          <FiltroSelect
            label="IP do usuário"
            value={f.ip}
            options={opcoes.ips}
            onChange={setSel('ip')}
          />
          <FiltroSelect
            label="Localização no sistema"
            value={f.localizacao}
            options={opcoes.services}
            onChange={setSel('localizacao')}
          />
          <FiltroSelect
            label="Atividades"
            value={f.atividades}
            options={opcoes.actions}
            onChange={setSel('atividades')}
          />
        </div>
        <div className="flex flex-col gap-2 border-t border-agnes-border px-4 py-3">
          <AgnesButton variant="primary" onClick={aplicar} className="w-full">
            Aplicar filtro
          </AgnesButton>
          <AgnesButton
            variant="outlined"
            onClick={limpar}
            startIcon={<Trash2 className="h-4 w-4" />}
            className="w-full"
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}

export default function PageAuditoria() {
  const lista = useAuditoriaStore((s) => s.lista);
  const carregando = useAuditoriaStore((s) => s.carregando);
  const page = useAuditoriaStore((s) => s.page);
  const size = useAuditoriaStore((s) => s.size);
  const total = useAuditoriaStore((s) => s.total);

  React.useEffect(() => {
    useAuditoriaStore.getState().carregarOpcoes();
    useAuditoriaStore.getState().buscar();
  }, []);

  // Export usa os filtros já aplicados no store (via popover "Aplicar filtro").
  const exportar = () => useAuditoriaStore.getState().exportar();

  const columns = [
    { key: 'data', header: 'Data', width: 110, render: (r: Item) => dataBR(r.createdAt) },
    { key: 'hora', header: 'Hora', width: 100, render: (r: Item) => horaBR(r.createdAt) },
    { key: 'ip', header: 'IP', width: 130, render: (r: Item) => r.ipAddress ?? '-' },
    { key: 'usuario', header: 'Usuário', width: 200, render: (r: Item) => r.username ?? '-' },
    { key: 'service', header: 'Localização', width: 180, render: (r: Item) => r.service ?? '-' },
    { key: 'method', header: 'Método', width: 100, render: (r: Item) => r.method ?? '-' },
    { key: 'action', header: 'Atividade', width: 200, render: (r: Item) => r.action ?? '-' },
    { key: 'path', header: 'Caminho', width: 260, render: (r: Item) => r.path ?? '-' },
  ];

  return (
    <>
      <AgnesPageHeader
        icon={<ScrollText />}
        title="Auditoria"
        subtitle="Registro de atividades do sistema"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Administração' },
          { label: 'Auditoria' },
        ]}
      />

      <AgnesCard>
        <AgnesCardContent className="flex items-stretch">
          <div className="flex justify-end gap-4 mb-2">
            <AuditoriaFiltro />

            <ExportButton
              variant="outlined"
              startIcon={<Download className="h-4 w-4" />}
              onClick={exportar}
            >
              Exportar
            </ExportButton>
          </div>

          {lista.length === 0 ? (
            <EmptyState
              icon={<ScrollText className="h-7 w-7" />}
              title="Nenhum registro de auditoria"
            />
          ) : (
            <TableWrapper>
              <AgnesDataTable<Item>
                columns={columns}
                data={lista}
                rowKey={(r) => String(r.id)}
                loading={carregando}
                pagination={{
                  page,
                  pageSize: size,
                  total,
                  serverSide: true,
                  onPageChange: (p) => useAuditoriaStore.getState().setPage(p),
                  onPageSizeChange: (s) => useAuditoriaStore.getState().setSize(s),
                }}
              />
            </TableWrapper>
          )}
        </AgnesCardContent>
      </AgnesCard>
    </>
  );
}
